import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Skeleton, Text } from '@client/toolkit';

import { SpaceTreeTag, SpaceTreeTagColor, useSpaceTreeTags } from '../hooks/use-space-tree-tags';

type SpaceTreeTagItemProps = {
  value: SpaceTreeTag;
  label?: React.ReactNode;
  active?: boolean;
  loading?: boolean;
  onClick?: (tag: SpaceTreeTag) => void;
  color: SpaceTreeTagColor;
};

function SpaceTreeTagItem({ value, label, active, loading, onClick, color }: SpaceTreeTagItemProps) {
  return (
    <button
      type="button"
      className={classNames(
        'border-2 text-xs leading-none font-normal tracking-wide rounded hover:bg-brand-primary-hover hover:text-black active:bg-brand-primary-active px-3 py-1',
        !active && 'bg-button',
        active ? 'text-black' : 'text-white',
        color === 'teal' && classNames('border-brand-primary', active && 'bg-brand-primary'),
      )}
      onClick={() => typeof onClick === 'function' && onClick(value)}
    >
      {!loading ? label : <Skeleton variant="text" className="w-20" />}
    </button>
  );
}

export type SpaceTreeDisplayProps = {
  open?: boolean;
};

export function SpaceTreeDisplay({ open }: SpaceTreeDisplayProps) {
  const { t, ready } = useTranslation('rentalObjects');

  const { TagOptions, toggleTag, isTagActive } = useSpaceTreeTags();

  return (
    <div
      className={classNames(
        'px-3 py-3 my-2 bg-surface-base border border-solid border-border rounded-lg',
        !open && 'hidden',
      )}
    >
      <Text
        as="div"
        loading={!ready}
        className="text-[0.8rem] font-semibold text-white mb-3"
        SkeletonProps={{ className: 'w-[120px]' }}
      >
        {t('highlightRentalObjects')}
      </Text>
      <div className="flex items-center space-x-2">
        {TagOptions.map((tag) => (
          <SpaceTreeTagItem
            key={tag.value}
            value={tag.value}
            label={t(`displayTag.${tag.value}`)}
            onClick={toggleTag}
            active={isTagActive(tag.value)}
            loading={!ready}
            color={tag.color}
          />
        ))}
      </div>
    </div>
  );
}
